import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, TrophyIcon } from "lucide-react";
import { useHackathon } from "../../contexts/HackathonContext";
import { criteria } from "../../data/program";
import { rankGroups } from "../../utils/judging";
export function Leaderboard() {
  const {
    state,
    clearScores
  } = useHackathon();
  const [expanded, setExpanded] = useState<number | null>(null);
  const ranking = rankGroups(state.scores);
  return <section aria-labelledby="leaderboard-heading" data-id="element-34" data-mp="38uzv3mwgmu8l">
      <div className="flex flex-wrap items-baseline justify-between gap-3 pb-3" data-id="element-35" data-mp="38sdlzitx86o9">
        <h2 id="leaderboard-heading" className="text-lg font-semibold" data-id="element-36" data-mp="2uw9v9zi9aggj">
          AI leaderboard
        </h2>
        {ranking.length > 0 && <button type="button" onClick={() => window.confirm("Clear all AI scores and pitch progress?") && clearScores()} className="text-sm text-muted transition-colors duration-150 hover:text-danger" data-id="element-37" data-mp="t3c74gzfcc1x">
            Clear scores
          </button>}
      </div>

      {ranking.length === 0 ? <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line px-6 py-14 text-center" data-id="element-38" data-mp="2t4m8ol7nmkyi">
          <TrophyIcon className="h-6 w-6 text-muted" aria-hidden="true" data-id="element-39" data-mp="36yd6wxfxqiex" />
          <p className="mt-3 text-[15px] font-medium" data-id="element-40" data-mp="31y7mo2bzvy6v">No pitches scored yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted" data-id="element-41" data-mp="2e8mq2gmx52f4">
            After each pitch, run the AI evaluation. Rankings and prizes update here as scores come in.
          </p>
        </div> : <ol className="divide-y divide-line rounded-xl border border-line bg-surface" data-id="element-42" data-mp="2o6u4arptp6df">
          {ranking.map(row => {
        const score = state.scores[row.groupId];
        const profile = state.groups.find(g => g.id === row.groupId);
        const open = expanded === row.groupId;
        const podium = row.place <= 3;
        return <li key={row.groupId} data-id="element-43" data-mp="6hy6er8kep24">
                <button type="button" onClick={() => setExpanded(open ? null : row.groupId)} aria-expanded={open} className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors duration-150 hover:bg-paper" data-id="element-44" data-mp="6gveqh3f4il5">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${row.place === 1 ? "bg-bryellow text-navy" : podium ? "bg-navy text-white" : "bg-paper text-muted"}`} data-id="element-45" data-mp="kf3k9deiss7x">
                    {row.place}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-46" data-mp="1jdfxjj47jqv">
                    <p className="truncate text-[15px] font-semibold" data-id="element-47" data-mp="1k52y5ync95j">
                      Group {row.groupId}
                      {profile?.productName && <span className="font-normal text-muted" data-id="element-48" data-mp="356n6s31hi9mr"> · {profile.productName}</span>}
                    </p>
                    <p className="truncate text-sm text-muted" data-id="element-49" data-mp="2xml1k3vk55uc">{row.prize?.amount}</p>
                  </div>
                  <span className="text-2xl font-bold tabular-nums" data-id="element-50" data-mp="2spcebovjybm8">{row.total.toFixed(1)}</span>
                  <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" data-id="element-51" data-mp="2no56xqxbneg3" />
                </button>
                <AnimatePresence initial={false} data-id="element-52" data-mp="2equysefsw5fe">
                  {open && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: "auto",
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden" data-id="element-53" data-mp="29x0cd38t9ccm">
                      <div className="space-y-3 px-5 pb-5 pl-[72px]" data-id="element-54" data-mp="1r0ify8j0ooa1">
                        <p className="text-sm font-medium" data-id="element-55" data-mp="2fqdbdc3etrry">{score.summary}</p>
                        {criteria.map(c => {
                  const cs = score.criteria[c.id];
                  return <div key={c.id} data-id="element-56" data-mp="26yl3n95rswr2">
                              <div className="flex items-baseline justify-between text-sm" data-id="element-57" data-mp="26vp4xim3m41k">
                                <span className="font-medium" data-id="element-58" data-mp="2axk34e8qg1b4">
                                  {c.label} <span className="text-muted" data-id="element-59" data-mp="1wyu9j8l4gjri">· {c.weight}%</span>
                                </span>
                                <span className="tabular-nums" data-id="element-60" data-mp="22005dfa2wn59">{cs.score.toFixed(1)}/10</span>
                              </div>
                              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line" data-id="element-61" data-mp="1s0j0h02nidac">
                                <div className="h-full rounded-full bg-brgreen" style={{
                        width: `${cs.score * 10}%`
                      }} data-id="element-62" data-mp="3icyoe5588w2v" />
                              </div>
                              <p className="mt-1 text-sm text-muted" data-id="element-63" data-mp="3me0dvu9cdtrn">{cs.rationale}</p>
                            </div>;
                })}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
      })}
        </ol>}
    </section>;
}